import React from 'react';
import { connect } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import format from 'date-fns/format';

import {
    Button,
    TextButton,
    Text,
} from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

// Constants
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Components
import TableMFH from 'app/shared/components/TableMFH';
import BillingTopBanner from 'app/shared/components/Billing/BillingTopBanner';
import BillingFilter from 'app/shared/components/Billing/BillingFilter';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import BillingError from 'app/shared/components/Billing/BillingError';
import BillingViewAll from 'app/shared/components/Billing/BillingViewAll';
import BillingEmptyBanner from 'app/shared/components/Billing/BillingEmptyBanner';
import BillingTableErrorRow from 'app/shared/components/Billing/BillingTableErrorRow';

import billingUtils from 'app/shared/utils/billingUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import { AppSelectors, UserSelectors } from 'app/shared/selectors';
import { RMWContext } from 'app/shared/providers/RMWContext';
import accessUtils from 'app/shared/utils/accessUtils';

// Stylesheets
import './style.scss';

const formatDate = value => value && format(new Date(value), 'MM/DD/YYYY hh:mm aa');

const rowSelectionDisabled = rowData => rowData.invoiceType
    === BillingConstants.INVOICE_TYPES.NUCLEUS_LISTING;

const propTypes = {
};

const BillingInvoicePage = ({
    loadingState,
    errorMsg,
    filters,
    invoices,
    hasMoreData,
    isScreenMobile,
    isPaymentDue,
    isFilterEnabled,
    fullColumnConfig,
    mobileColumnConfig,
    setFilters,
    fetchBillingData,
    fetchPropertyData,
    clearInvoices,
    groupingHeaderRenderer,
    groupingFunction,
    emptyMessage,
    title,
    user,
    showHistory,
}) => {
    const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user });
    const { paths } = React.useContext(RMWContext);
    const isNucleusOnly = billingUtils.isOnlyNucleusUser(user);
    const tempColumnConfig = isScreenMobile ? mobileColumnConfig : fullColumnConfig;
    const columnConfig = cloneDeep(tempColumnConfig);

    const filteredInvoices = invoices.slice(0);
    const [selected, setSelected] = React.useState(new Set());
    const [isViewAllClicked, setViewAll] = React.useState(false);

    const handlePayInvoiceClick = (invoiceId) => {
        window.router.transitionTo(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
    };

    const handleEditClick = (accountNumber) => {
        window.router.transitionTo(`${paths.billingPage}/account/${accountNumber}`);
    };

    // The click action requires to live within a component
    if (isPaymentDue && paymentsEnabled) {
        columnConfig.push({
            width: 1,
            dataKey: 'autoPay',
            header: 'Auto Pay',
            renderer: (invoice) => {
                if (!invoice.canMakePayments) {
                    return '-';
                }
                if (billingUtils.invoiceInAutopayRange(invoice)) {
                    return (
                        <TextButton onClick={() => handleEditClick(invoice.accountNumber)}>
                            {invoice.accountAutopay ? 'ON' : 'OFF'}
                        </TextButton>
                    );
                }
                return '-';
            },
            align: 'center',
        });
        columnConfig.push({
            width: 1,
            dataKey: 'action',
            header: '',
            renderer: invoice => invoice.canMakePayments && (
                <Button buttonType="primary" onClick={() => handlePayInvoiceClick(invoice.invoiceNumber)}>
                    Pay
                </Button>
            ),
            align: 'right',
        });
    }

    const handleSaveFilters = (newFilters) => {
        setFilters(newFilters);
        setSelected(new Set());
        fetchBillingData(true);
    };

    const handleInfiniteScroll = (isVisible) => {
        if (isVisible && hasMoreData) {
            fetchBillingData();
        }
    };

    React.useEffect(() => {
        fetchBillingData(true);
        fetchPropertyData();
        return () => {
            clearInvoices();
        };
    }, []);

    const renderEmptyBanner = () => {
        if (filteredInvoices.length === 0 && loadingState === LoadingConstants.SUCCESS) {
            let emptyResultsBanner;
            if (isFilterEnabled) {
                emptyResultsBanner = BillingConstants.EMPTY_NOTIFICATIONS.NO_INVOICES_WITH_FILTER;
            } else {
                emptyResultsBanner = emptyMessage;
            }
            return (
                <BillingEmptyBanner
                    notification={emptyResultsBanner}
                />
            );
        }
        return null;
    };

    const historyColumnConfig = [
        {
            dataKey: '-',
            width: 1,
            header: '',
            renderer: () => ' ',
            align: 'right',
        },
        {
            dataKey: 'date',
            width: 2,
            header: 'Payment Date',
            renderer: value => formatDate(value.createdTime),
            align: 'left',
        },
        {
            width: 3,
            header: 'Amount Paid',
            renderer: value => numberUtils.asCurrency(value.appliedPaymentAmount, '$0,0.00'),
            align: 'center',
        },
        {
            width: 3,
            header: 'Payment Number',
            dataKey: 'paymentNumber',
            align: 'center',
        },
        {
            width: 3,
            header: 'Transaction Status',
            dataKey: 'status',
            align: 'center',
            renderer: (value) => {
                const { status, gatewayResponseCode } = value;
                if (status === 'processed') {
                    return 'Complete';
                }
                let customMessage = '';
                if (gatewayResponseCode) {
                    const errorMessage = BillingConstants.RESPONSE_CODE_MESSAGES[gatewayResponseCode];
                    if (errorMessage) {
                        customMessage = `: ${errorMessage}`;
                    }
                }
                if (status.length) {
                    return status[0].toUpperCase() + status.slice(1) + customMessage;
                }
                return '-';
            },
        },
    ];

    if (paymentsEnabled) {
        historyColumnConfig.splice(1, 0, {
            width: 3,
            header: 'Payment Method',
            align: 'center',
            renderer: billingUtils.formatPaymentMethod,
        });
        historyColumnConfig.push({
            width: 3,
            header: 'Paid By',
            align: 'center',
            renderer: value => (value.email ? value.email : '-'),
        });
    }

    const renderExpandibleComponent = rowData => (
        <div>
            <div className="TableMFH-header-row">
                <div className="TableMFH-col" style={{ width: `${1 / 12 * 100}%` }} />
                <div className="TableMFH-col" style={{ width: `${11 / 12 * 100}%` }}>
                    <div className="TableMFH-header-cell-inner payment-history-header">
                        Payment History
                    </div>
                </div>
            </div>
            <TableMFH
                columnConfig={historyColumnConfig}
                data={rowData.payments.slice().sort((a, b) => b.createdTime - a.createdTime)}
                stickyHeader={false}
            />
        </div>
    );

    const renderContent = () => {
        const data = isViewAllClicked ? filteredInvoices : filteredInvoices.slice(0, 8);
        return (
            <React.Fragment>
                <TableMFH
                    idKey="invoiceId"
                    columnConfig={columnConfig}
                    data={data}
                    filters={filters}
                    selected={selected}
                    onSaveFilters={handleSaveFilters}
                    onSelectRow={setSelected}
                    selectionDisabled={isNucleusOnly}
                    rowSelectionDisabled={rowSelectionDisabled}
                    TopBannerComponent={!isNucleusOnly ? BillingTopBanner : undefined}
                    FilterComponent={BillingFilter}
                    hasMoreData={isViewAllClicked && hasMoreData && loadingState !== LoadingConstants.ERROR}
                    ErrorRowComponent={loadingState === LoadingConstants.ERROR ? BillingTableErrorRow : undefined}
                    handleLoadMoreData={handleInfiniteScroll}
                    isLoading={loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL}
                    isMobile={isScreenMobile}
                    groupingFunction={groupingFunction}
                    groupingHeaderRenderer={groupingHeaderRenderer}
                    isRowExpandable={rowData => showHistory && rowData.payments && rowData.payments.length > 0}
                    ExpandedComponent={renderExpandibleComponent}
                />
                {renderEmptyBanner()}
                {!isViewAllClicked && (filteredInvoices.length > 8 || hasMoreData) && (
                    <BillingViewAll onClick={() => setViewAll(true)} />
                )}
            </React.Fragment>
        );
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="BillingInvoicePage">
                <HeadWrapper title={title} />
                {errorMsg && <BillingError errorMsg={errorMsg} />}
                {loadingState === LoadingConstants.ERROR && (
                    <BillingError errorMsg={BillingConstants.INVOICE_LOAD_ERROR} />
                )}
                {renderContent()}
                {!isNucleusOnly && (
                    <Row className="BillingInvoicePage-Footer">
                        <Col className="BillingInvoicePage-col" xs={12}>
                            {!paymentsEnabled && (
                                <Text fontType="finePrint" as="div">
                                    {BillingConstants.BILLING_INFO_FOOTNOTE}
                                </Text>
                            )}
                        </Col>
                    </Row>
                )}
            </div>
        </AuthCheckWrapper>
    );
};

BillingInvoicePage.propTypes = propTypes;

const mapStateToProps = state => ({
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    user: UserSelectors.makeUser(state),
});

export { BillingInvoicePage };
export default connect(mapStateToProps)(BillingInvoicePage);
